import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface Tag {
  id: string;
  account_id: string;
  name: string;
  color: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function useTags() {
  const { user } = useAuth();
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTags = useCallback(async () => {
    if (!user?.account?.id || !supabase) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('account_tags')
        .select('*')
        .eq('account_id', user.account.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setTags(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.account?.id]);

  const createTag = async (tag: Omit<Tag, 'id' | 'account_id' | 'created_at' | 'updated_at'>) => {
    if (!user?.account?.id || !supabase) return null;

    try {
      const { data, error: createError } = await supabase
        .from('account_tags')
        .insert({
          account_id: user.account.id,
          ...tag
        })
        .select()
        .single();

      if (createError) throw createError;

      setTags(prev => [data, ...prev]);
      return data;
    } catch (err: any) {
      setError(err.message);
      return null;
    }
  };

  const updateTag = async (id: string, updates: Partial<Tag>) => {
    if (!supabase) return false;

    try {
      const { error: updateError } = await supabase
        .from('account_tags')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (updateError) throw updateError;

      setTags(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  const deleteTag = async (id: string) => {
    if (!supabase) return false;

    try {
      const { error: deleteError } = await supabase
        .from('account_tags')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      setTags(prev => prev.filter(t => t.id !== id));
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  return {
    tags,
    loading,
    error,
    fetchTags,
    createTag,
    updateTag,
    deleteTag
  };
}
